import type React from 'react';
import type Luminary from '@lumen5/luminary';
import type { Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    advancedMediaControlsAssetId,
    advancedMediaControlsSceneId,
} from 'js/src/data/app/app-selectors';
import { setBrightnessUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data/video-data-actions';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import {
    DEFAULT_BLUR,
    DEFAULT_BRIGHTNESS,
    DEFAULT_CONTRAST,
    DEFAULT_ENABLE_GREYSCALE,
    DEFAULT_HUE,
    DEFAULT_LIGHTNESS,
    DEFAULT_NOISE,
    DEFAULT_NOISE_SIZE,
    DEFAULT_PLAYBACK_RATE,
    DEFAULT_SATURATION,
    DEFAULT_VIGNETTING,
    DEFAULT_VIGNETTING_ALPHA,
    DEFAULT_VIGNETTING_BLUR,
} from 'js/src/libs/consts';
import { getAdvancedMediaControlsAsset } from 'js/src/luminary/luminary-selectors';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { debounceOneSecond } from 'js/src/luminary/luminary-operations/debounce-checkpoint';

function checkpoint(
    dispatch: AppDispatch,
    luminary: Luminary,
    systemTriggered: boolean,
    parentActionGroup?: string,
) {
    dispatch(
        checkpointVideo({
            video_data: saveRawLuminary(luminary),
            systemTriggered,
            parentActionGroup,
        }),
    );
}

function resolveSceneMediaAsset(scene: Scene, assetId: string): TimeAwareMediaAsset {
    const mediaAsset = scene.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
    if (!mediaAsset) throw new Error('Media asset not found in scene');
    return mediaAsset;
}

function resolveAdvancedMediaAsset(luminary: Luminary, getState: GetRootState) {
    const state = getState();
    return getAdvancedMediaControlsAsset(
        luminary,
        advancedMediaControlsSceneId(state),
        advancedMediaControlsAssetId(state),
    );
}

function makeAdvancedMediaControlOperation(
    eventName: string,
    apply: (mediaAsset: TimeAwareMediaAsset, value: number) => void,
) {
    return createLuminaryOperation(
        (
            { luminary, dispatch, getState },
            value: number,
            systemTriggered = false,
            parentActionGroup?: string,
        ) => {
            const { mediaAsset, mediaContainer } = resolveAdvancedMediaAsset(luminary, getState);
            if (!mediaAsset) {
                return;
            }

            apply(mediaAsset, value);

            debounceOneSecond(() => {
                analytics.track(eventName, {
                    isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
                });
                checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
            });
        },
    );
}

export const setSceneGroupAvatarBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !sceneGroup.avatar) {
            return;
        }
        sceneGroup.avatar.brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Avatar Brightness Updated', {
                isSceneGroupAvatar: true,
                isFromAdvancedControls,
            });
            checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
        });
    },
);

export const setSceneGroupMediaBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !sceneGroup.mediaAsset) {
            return;
        }
        sceneGroup.mediaAsset.brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Media Asset Brightness Updated', {
                isSceneGroupMedia: true,
                isFromAdvancedControls,
            });
            checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
        });
    },
);

export const setSceneMediaPlaybackRateOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        playbackRate: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        resolveSceneMediaAsset(scene, assetId).playbackRate = playbackRate;

        debounceOneSecond(() => {
            analytics.track('Media Asset Playback Rate Updated', {
                isSceneGroupMedia: false,
                isFromAdvancedControls,
            });
            checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
        });
    },
);

// A toggle is a single discrete change rather than a slider drag, so it checkpoints straight away
// instead of debouncing the way its neighbours do.
export const setSceneMediaPlaybackModeOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        playbackMode: string,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        resolveSceneMediaAsset(scene, assetId).playbackMode = playbackMode;

        analytics.track('Media Asset Playback Mode Updated', {
            isSceneGroupMedia: false,
            isFromAdvancedControls,
            playbackMode,
        });
        checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
    },
);

export const setSceneMediaBrightnessOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        brightness: number,
        isFromAdvancedControls = false,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        resolveSceneMediaAsset(scene, assetId).brightness = brightness;

        debounceOneSecond(() => {
            analytics.track('Media Asset Brightness Updated', {
                isSceneGroupMedia: false,
                isFromAdvancedControls,
            });
            checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
            dispatch(
                setBrightnessUserOverride({
                    sceneId: scene.id,
                    isAutomatic: false,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        });
    },
);

export const setAdvancedMediaControlsContrastOperation = makeAdvancedMediaControlOperation(
    'Media Asset Contrast Updated',
    (mediaAsset, contrast) => {
        mediaAsset.contrast = contrast;
    },
);

export const setAdvancedMediaControlsHueOperation = makeAdvancedMediaControlOperation(
    'Media Asset Hue Updated',
    (mediaAsset, hue) => {
        mediaAsset.hue = hue;
    },
);

export const setAdvancedMediaControlsSaturationOperation = makeAdvancedMediaControlOperation(
    'Media Asset Saturation Updated',
    (mediaAsset, saturation) => {
        mediaAsset.saturation = saturation;
    },
);

export const setAdvancedMediaControlsLightnessOperation = makeAdvancedMediaControlOperation(
    'Media Asset Lightness Updated',
    (mediaAsset, lightness) => {
        mediaAsset.lightness = lightness;
    },
);

export const setAdvancedMediaControlsBlurOperation = makeAdvancedMediaControlOperation(
    'Media Asset Blur Updated',
    (mediaAsset, blur) => {
        mediaAsset.blur = blur;
    },
);

export const setAdvancedMediaControlsVignettingOperation = makeAdvancedMediaControlOperation(
    'Media Asset Vignetting Updated',
    (mediaAsset, vignetting) => {
        mediaAsset.vignetting = vignetting;
        // these options are hard coded for now and not exposed to the user
        mediaAsset.vignettingBlur = DEFAULT_VIGNETTING_BLUR;
        mediaAsset.vignettingAlpha = DEFAULT_VIGNETTING_ALPHA;
    },
);

export const setAdvancedMediaControlsNoiseOperation = makeAdvancedMediaControlOperation(
    'Media Asset Noise Updated',
    (mediaAsset, noise) => {
        mediaAsset.noise = noise;
        // hard coded for now and not exposed to the user
        mediaAsset.noiseSize = DEFAULT_NOISE_SIZE;
    },
);

export const resetAdvancedMediaControlsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        _event: React.MouseEvent<HTMLElement>,
        systemTriggered = false,
        parentActionGroup?: string,
    ) => {
        const { mediaAsset, mediaContainer } = resolveAdvancedMediaAsset(luminary, getState);
        if (!mediaAsset) {
            return;
        }

        luminary.batchRun(() => {
            // colour correction
            mediaAsset.brightness = DEFAULT_BRIGHTNESS;
            mediaAsset.contrast = DEFAULT_CONTRAST;
            mediaAsset.hue = DEFAULT_HUE;
            mediaAsset.saturation = DEFAULT_SATURATION;
            mediaAsset.lightness = DEFAULT_LIGHTNESS;
            // effects
            mediaAsset.blur = DEFAULT_BLUR;
            mediaAsset.vignetting = DEFAULT_VIGNETTING;
            mediaAsset.vignettingBlur = DEFAULT_VIGNETTING_BLUR;
            mediaAsset.vignettingAlpha = DEFAULT_VIGNETTING_ALPHA;
            mediaAsset.noise = DEFAULT_NOISE;
            mediaAsset.noiseSize = DEFAULT_NOISE_SIZE;
            mediaAsset.enableGreyscale = DEFAULT_ENABLE_GREYSCALE;
            mediaAsset.playbackRate = DEFAULT_PLAYBACK_RATE;
            mediaAsset.playbackMode = VideoData.PLAYBACK_LOOP;
        });

        analytics.track('Advanced Media Controls Reset', {
            isSceneGroupMedia: VideoData.isSceneGroup(mediaContainer),
        });
        checkpoint(dispatch, luminary, systemTriggered, parentActionGroup);
    },
);
